import { useCallback, useState } from 'preact/hooks';

/**
 * A single on/off display preference, persisted in `localStorage`.
 *
 * Deliberately not in the repository: this is how the app is *displayed*, not the
 * instructor's data, so it has no business in the schedule that Phase 2 would sync
 * across devices — the right setting on a phone may be the wrong one on a tablet.
 *
 * Reads and writes are wrapped because `localStorage` throws in private-mode
 * Safari, and losing a display preference must never break the screen.
 */
export function usePreference(
  key: string,
  fallback: boolean,
): readonly [boolean, (value: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored === null ? fallback : stored === 'true';
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // A preference that cannot be saved still applies for this session.
      }
    },
    [key],
  );

  return [value, update];
}

/** Whether the schedule list shows days with nothing on them. Off by default. */
export const SHOW_EMPTY_DAYS_KEY = 'yoga.showEmptyDays';

/**
 * A single numeric display preference, persisted in `localStorage`.
 *
 * Same rationale and Safari-private-mode safety as {@link usePreference}, but
 * for a small set of numeric choices (e.g. reminder lead time in minutes).
 */
export function useNumberPreference(
  key: string,
  fallback: number,
): readonly [number, (value: number) => void] {
  const [value, setValue] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored === null) {
        return fallback;
      }
      const parsed = Number(stored);
      return Number.isFinite(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  });

  const update = useCallback(
    (next: number) => {
      setValue(next);
      try {
        localStorage.setItem(key, String(next));
      } catch {
        // A preference that cannot be saved still applies for this session.
      }
    },
    [key],
  );

  return [value, update];
}

/** Whether calendar exports include a reminder. On by default. */
export const REMINDER_ENABLED_KEY = 'yoga.reminderEnabled';
/** Minutes before a class that the reminder fires. */
export const REMINDER_MINUTES_KEY = 'yoga.reminderMinutes';
/** The lead-time choices offered in the UI. */
export const REMINDER_CHOICES = [5, 10, 15, 30, 60] as const;
export const DEFAULT_REMINDER_MINUTES = 15;
